<template>
  <modal
    width="609px"
    height="500px"
    bodyPadding="0"
    :title="t('Integration')"
    @close="close"
    read-article-url="https://help.bemob.com/docs/advanced-integrations-introduction"
  >
    <div class="choose-integration-dialog__body">
      <ui-input
        v-model="search"
        class="search"
        hideDetails="1"
        :placeholder="t('Search')"
        prepend-inner-icon="fas fa-search"
      />
      <div class="list">
        <div class="no-data-found" v-if="integrationsListFiltered.length === 0">
          {{ t('No Data Found') }}
        </div>
        <button
          type="button"
          v-for="integration in integrationsListFiltered"
          :key="integration.type"
          @click="selectIntegration(integration)"
          :class="{
            integration: true,
            integration__active: integration.type === selectedIntegration?.type,
          }"
        >
          <img
            :alt="integration.name"
            :style="integration.img.style"
            :src="'/images/integrations/' + integration.img.src"
          />
          <ui-plan-limits
            v-if="hasPlanLimitsRestriction(integrationsPlanLimits(integration.type))"
            class="plan-limits-icon"
          />
        </button>
      </div>
    </div>

    <template #footer>
      <div class="align-center xs12 error-wrapper justify-end d-flex">
        <div class="d-flex">
          <ui-button
            :text="t('Cancel')"
            icon="fas fa-times"
            :disabled="loading"
            @click="close"
          />

          <ui-button
            :disabled="!selectedIntegration"
            :text="t('Next')"
            icon="fas fa-arrow-right"
            button-style="accent"
            :loading="loading"
            @click="create"
          />
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n, useIntegrations, usePlanLimits } from '@/composables';

const props = defineProps<{
  data?: {
    createIntegrationOpen?: (payload: Record<string, unknown>) => void;
  };
}>();

const emit = defineEmits<{ close: [] }>();

const { t } = useI18n();
const { integrationsData } = useIntegrations();
const { hasPlanLimitsRestriction, integrationsPlanLimits, handleClickPlanLimits } =
  usePlanLimits(undefined, false);

const search = ref('');
const selectedIntegration = ref<Integration | null>(null);
const loading = ref(false);

const integrationsListFiltered = computed(() => {
  const items = Object.values(integrationsData.value);
  if (!search.value) return items;
  return items.filter(item =>
    item.name.toLowerCase().includes(search.value.toLowerCase()),
  );
});

const close = () => {
  emit('close');
};

const selectIntegration = (integration: Integration) => {
  const planLimits = integrationsPlanLimits(integration.type);
  if (hasPlanLimitsRestriction(planLimits)) {
    handleClickPlanLimits(planLimits);
    return;
  }
  selectedIntegration.value = integration;
};

const create = () => {
  if (!selectedIntegration.value) return;
  const { createIntegrationOpen } = props.data || {};
  close();
  createIntegrationOpen?.({
    type: selectedIntegration.value.type,
  });
};
</script>

<style lang="scss" scoped>
.choose-integration-dialog__body {
  display: block;
  height: 100%;
  background: var(--color-gray5);
}
.list {
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  overflow-y: auto;
  padding: 16px 8px 0;
}
.integration {
  width: calc(25% - 16px);
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--color-white);
  border-radius: 8px;
  height: 56px;
  margin: 0 8px 8px;
  cursor: pointer;
  border: 2px solid var(--color-white);
  position: relative;
  &__active {
    box-shadow: 0 0 8px 0 var(--color-green50);
    border: 2px solid var(--color-green);
  }
  &:disabled {
    opacity: 0.4;
  }

  img {
    max-width: 90%;
    max-height: 90%;
  }
  @media all and (max-width: 600px) {
    width: calc(33.3333333333333% - 16px);
  }
  @media all and (max-width: 500px) {
    width: calc(50% - 16px);
  }
  .plan-limits-icon {
    position: absolute;
    top: -8px;
    right: -7px;
  }
}

.search {
  width: 100%;
  padding: 16px 16px 0;
}
.no-data-found {
  margin: 0 8px;
}
</style>
